import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PageHeader from 'components/common/PageHeader';
import FullPageSpinner from 'components/common/full-page-spinner';
import Error404 from 'components/errors/Error404';
import AppContext from 'context/Context';
import { swalToastError } from 'components/utilities/SwalToastUtil';
import { getOptionsByKey } from 'services/optionLists';
import { get as getKpiBoard } from 'services/bs-kpi-board/kpiBoardService';
import { getSectors } from 'services/bs-sector-measurable/sectorService';
import { search as searchMeasurables } from 'services/bs-sector-measurable/measurableService';
import { search as searchTargets } from 'services/bs-sector-measurable/targetService';
import {
  search as searchMeasurableData,
  upSert as upsertMeasurableData,
  remove as removeMeasurableData
} from 'services/bs-sector-measurable/measurableDataService';
import { targetTypeKey } from 'helpers/kpiHelper';
import {
  buildMonthStartsOfWeek,
  buildSicSectors,
  buildWeekDates,
  DAILY_SCHEDULE_TYPE_NAME,
  DATE_TARGET_TYPE_NAME,
  DEFAULT_WEEK_START_DAY,
  SIC_API_DATE_FORMAT,
  SIC_KPI_BOARD_ID,
  TIME_TARGET_TYPE_NAME,
  WEEK_START_DAY_OPTIONS,
  YES_NO_TARGET_TYPE_NAME
} from 'helpers/sicHelper';
import SectorModal from '../kpi-details/SectorModal';
import { buildSicTableStyles } from './sicTableStyles';
import SicSummaryCard from './SicSummaryCard';
import SicSectorCard, { buildSicCellKey } from './SicSectorCard';
import SicMeasurableModal from './SicMeasurableModal';
import SicTargetModal from './SicTargetModal';

const SECTOR_SCHEDULE_TYPE_KEY = 'balance.score.card.sectorscheduletype';

const findOptionIdByName = (options, name) =>
  options?.find(
    option => option.name?.trim().toLowerCase() === name.toLowerCase()
  )?.id ?? null;

const EMPTY_TARGET_TYPE_IDS = { yesNo: null, date: null, time: null };

const sortSectorsByOrder = sectors =>
  [...sectors].sort(
    (a, b) =>
      (Number(a.orderNo ?? a.value2) || 0) - (Number(b.orderNo ?? b.value2) || 0)
  );

const replaceMeasurableValue = (dataItems, measurableId, dateText, value) => {
  const otherItems = dataItems.filter(
    item =>
      !(
        item.measurableId === measurableId &&
        item.date?.startsWith(dateText)
      )
  );
  return value === null
    ? otherItems
    : [...otherItems, { measurableId, date: dateText, value: String(value) }];
};

export default function SicScorecard() {
  const kpiBoardId = SIC_KPI_BOARD_ID;
  const {
    config: { isDark }
  } = useContext(AppContext);
  const styles = useMemo(() => buildSicTableStyles(isDark), [isDark]);

  const [board, setBoard] = useState(undefined);
  const [sectors, setSectors] = useState([]);
  const [measurables, setMeasurables] = useState([]);
  const [targets, setTargets] = useState([]);
  const [dataItems, setDataItems] = useState([]);
  const [targetTypeIds, setTargetTypeIds] = useState(EMPTY_TARGET_TYPE_IDS);
  const [dailyScheduleTypeId, setDailyScheduleTypeId] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStartDay, setWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);

  const [isBoardLoading, setIsBoardLoading] = useState(true);
  const [isMeasurablesLoading, setIsMeasurablesLoading] = useState(false);
  const [isWeekDataLoading, setIsWeekDataLoading] = useState(false);
  const [savingCellKeys, setSavingCellKeys] = useState(new Set());

  const [sectorBeingEdited, setSectorBeingEdited] = useState(null);
  const [measurableModalState, setMeasurableModalState] = useState(null);
  const [measurableForTarget, setMeasurableForTarget] = useState(null);

  const weekDates = useMemo(
    () => buildWeekDates(selectedDate, weekStartDay),
    [selectedDate, weekStartDay]
  );
  const weekDateTexts = useMemo(
    () => weekDates.map(date => date.format(SIC_API_DATE_FORMAT)),
    [weekDates]
  );
  const monthStartsOfWeek = useMemo(
    () => buildMonthStartsOfWeek(weekDates),
    [weekDates]
  );

  const loadSectors = useCallback(async () => {
    const response = await getSectors(kpiBoardId);
    setSectors(sortSectorsByOrder(response?.data ?? []));
  }, [kpiBoardId]);

  const loadMeasurables = useCallback(async () => {
    setIsMeasurablesLoading(true);
    const response = await searchMeasurables({
      kpiBoardId,
      isGetAll: true
    });
    setMeasurables(response?.items ?? []);
    setIsMeasurablesLoading(false);
  }, [kpiBoardId]);

  const scheduleTypeForData = dailyScheduleTypeId ?? sectors[0]?.scheduleType;

  const loadWeekData = useCallback(async () => {
    if (!scheduleTypeForData) return;
    setIsWeekDataLoading(true);
    const [targetResponse, dataResponse] = await Promise.all([
      searchTargets({
        kpiBoardId,
        dates: monthStartsOfWeek,
        isGetAll: true
      }),
      searchMeasurableData({
        kpiBoardId,
        scheduleType: scheduleTypeForData,
        dates: weekDateTexts,
        isGetAll: true
      })
    ]);
    setTargets(targetResponse?.items ?? []);
    setDataItems(dataResponse?.items ?? []);
    setIsWeekDataLoading(false);
  }, [kpiBoardId, scheduleTypeForData, monthStartsOfWeek, weekDateTexts]);

  useEffect(() => {
    const loadBoard = async () => {
      setIsBoardLoading(true);
      const [boardResponse, sectorResponse, targetTypeOptions, scheduleTypeOptions] =
        await Promise.all([
          getKpiBoard(kpiBoardId),
          getSectors(kpiBoardId),
          getOptionsByKey(targetTypeKey),
          getOptionsByKey(SECTOR_SCHEDULE_TYPE_KEY)
        ]);

      if (!boardResponse?.data) {
        setBoard(null);
        setIsBoardLoading(false);
        return;
      }

      setBoard(boardResponse.data);
      setWeekStartDay(boardResponse.data.weekStartDay ?? DEFAULT_WEEK_START_DAY);
      setSectors(sortSectorsByOrder(sectorResponse?.data ?? []));
      setTargetTypeIds({
        yesNo: findOptionIdByName(targetTypeOptions, YES_NO_TARGET_TYPE_NAME),
        date: findOptionIdByName(targetTypeOptions, DATE_TARGET_TYPE_NAME),
        time: findOptionIdByName(targetTypeOptions, TIME_TARGET_TYPE_NAME)
      });
      setDailyScheduleTypeId(
        findOptionIdByName(scheduleTypeOptions, DAILY_SCHEDULE_TYPE_NAME)
      );
      setIsBoardLoading(false);
    };
    loadBoard();
  }, [kpiBoardId]);

  useEffect(() => {
    if (board) loadMeasurables();
  }, [board, loadMeasurables]);

  useEffect(() => {
    if (board) loadWeekData();
  }, [board, loadWeekData]);

  const sicSectors = useMemo(
    () =>
      buildSicSectors({
        sectors,
        measurables,
        targets,
        dataItems,
        weekDates,
        targetTypeIds
      }),
    [sectors, measurables, targets, dataItems, weekDates, targetTypeIds]
  );

  const updateSavingCell = (cellKey, isSaving) =>
    setSavingCellKeys(previous => {
      const next = new Set(previous);
      if (isSaving) next.add(cellKey);
      else next.delete(cellKey);
      return next;
    });

  const handleCommitValue = async (measurable, dayIndex, value) => {
    const cellKey = buildSicCellKey(measurable.id, dayIndex);
    const dateText = weekDateTexts[dayIndex];
    updateSavingCell(cellKey, true);

    const response =
      value === null
        ? await removeMeasurableData(measurable.id, dateText, scheduleTypeForData)
        : await upsertMeasurableData(
            measurable.id,
            dateText,
            value,
            scheduleTypeForData
          );

    if (response?.success) {
      setDataItems(previous =>
        replaceMeasurableValue(previous, measurable.id, dateText, value)
      );
    } else {
      swalToastError(response?.message || 'Failed to save value.');
    }
    updateSavingCell(cellKey, false);
  };

  const openCreateMeasurable = sector => {
    const highestOrderNo = Math.max(
      0,
      ...sector.measurables.map(measurable => Number(measurable.orderNo) || 0)
    );
    setMeasurableModalState({
      measurable: {},
      sectorId: sector.id,
      nextOrderNo: highestOrderNo + 1
    });
  };

  const handleSectorSaved = () => {
    setSectorBeingEdited(null);
    loadSectors();
  };

  const handleMeasurableSaved = () => {
    setMeasurableModalState(null);
    loadMeasurables();
  };

  const handleTargetSaved = () => {
    setMeasurableForTarget(null);
    loadWeekData();
  };

  if (board === null) return <Error404 />;

  const isLoading = isBoardLoading || isMeasurablesLoading || isWeekDataLoading;

  return (
    <>
      <PageHeader
        title={board?.name ?? 'SIC Scorecard'}
        description={`${board?.description ?? ''} | Week ${weekDates[0].format(
          'DD MMM'
        )} - ${weekDates[6].format('DD MMM YYYY')}`}
        col="col-lg-12"
        className="mb-3 col-lg-12"
      />

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col xs={12} sm="auto">
              <Form.Label className="fs--1 mb-1">Week of</Form.Label>
              <DatePicker
                selected={selectedDate}
                calendarStartDay={weekStartDay}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                onChange={date => date && setSelectedDate(date)}
                disabled={isLoading}
              />
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label className="fs--1 mb-1">Week starts on</Form.Label>
              <Form.Select
                value={weekStartDay}
                onChange={e => setWeekStartDay(Number(e.target.value))}
                disabled={isLoading}
              >
                {WEEK_START_DAY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm="auto" className="ms-sm-auto">
              <Button
                variant="outline-primary"
                onClick={() => setSectorBeingEdited({})}
              >
                Create Sector
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!isBoardLoading && !sectors.length ? (
        <Card className="mb-3">
          <Card.Body className="text-muted">
            This board has no sectors yet. Click &quot;Create Sector&quot; to
            add one (use Schedule Type &quot;Daily&quot;).
          </Card.Body>
        </Card>
      ) : (
        <SicSummaryCard
          sectors={sicSectors}
          weekDates={weekDates}
          styles={styles}
        />
      )}

      {sicSectors.map(sector => (
        <SicSectorCard
          key={sector.id}
          sector={sector}
          weekDates={weekDates}
          styles={styles}
          savingCellKeys={savingCellKeys}
          onCommitValue={handleCommitValue}
          onEditSector={setSectorBeingEdited}
          onEditMeasurable={measurable =>
            setMeasurableModalState({
              measurable,
              sectorId: measurable.sectorId
            })
          }
          onCreateMeasurable={openCreateMeasurable}
          onEditTarget={setMeasurableForTarget}
        />
      ))}

      <FullPageSpinner show={isLoading} quantity={3} variant="success" />

      {sectorBeingEdited && (
        <SectorModal
          isShow
          data={sectorBeingEdited}
          kpiId={board?.id}
          handleClose={() => setSectorBeingEdited(null)}
          handleSubmit={handleSectorSaved}
        />
      )}

      {measurableModalState && (
        <SicMeasurableModal
          measurable={measurableModalState.measurable}
          sectorId={measurableModalState.sectorId}
          nextOrderNo={measurableModalState.nextOrderNo}
          targetTypeIds={targetTypeIds}
          handleClose={() => setMeasurableModalState(null)}
          handleSaved={handleMeasurableSaved}
        />
      )}

      {measurableForTarget && (
        <SicTargetModal
          measurable={measurableForTarget}
          weekDates={weekDates}
          monthStarts={monthStartsOfWeek}
          handleClose={() => setMeasurableForTarget(null)}
          handleSaved={handleTargetSaved}
        />
      )}
    </>
  );
}
